import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Delete16Regular, MoreHorizontal20Regular, Rename16Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import type { Device, DeviceStatus, Me } from '../../api/types';
import { PresenceDot } from '../../components/PresenceDot';
import { formatDateTime } from '../../lib/format';
import { vt } from '../../theme';
import { AddressFlyout, PinMark } from './AddressFlyout';
import { RenameDialog, RevokeDialog } from './DeviceDialogs';
import { statusCells } from './model';
import { TagsPopover } from './TagsPopover';

const useStyles = makeStyles({
  table: { tableLayout: 'fixed', width: '100%' },
  head: {
    height: '30px',
    fontSize: '11.5px',
    color: vt.fg3,
    fontWeight: tokens.fontWeightRegular,
    borderBottom: `1px solid ${vt.surface2}`,
  },
  row: { height: '38px', borderBottom: `1px solid ${vt.surface2}` },
  cell: { paddingBlock: 0, boxSizing: 'border-box', overflow: 'hidden', whiteSpace: 'nowrap' },
  end: { textAlign: 'end', '& > *': { justifyContent: 'flex-end' } },
  dot: { marginInline: 'auto' },
  name: { display: 'flex', flexDirection: 'column', lineHeight: '15px', minWidth: 0 },
  nameText: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1, overflow: 'hidden', textOverflow: 'ellipsis' },
  dns: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10.5px', color: vt.fg3, overflow: 'hidden', textOverflow: 'ellipsis' },
  address: { display: 'inline-flex', alignItems: 'center', columnGap: '8px' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg2 },
  chips: { display: 'flex', alignItems: 'center', columnGap: '6px', overflow: 'hidden' },
  owner: { color: vt.fg2, overflow: 'hidden', textOverflow: 'ellipsis' },
  muted: { color: vt.fg3 },
});

const useChipStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.tintNeutral,
    color: vt.fg3,
    fontSize: '10.5px',
    lineHeight: '18px',
    whiteSpace: 'nowrap',
  },
});

/** A network or tag label: a small neutral pill, as the mockup draws them. */
export function Chip({ children }: { children: string }) {
  return <span className={useChipStyles().chip}>{children}</span>;
}

interface Props {
  devices: Device[];
  /** Every device the caller can see (the flyout's "in use" check spans all of them, not just the filtered rows). */
  allDevices: Device[];
  cidrOf: (network: string | null) => string | null;
  me: Me;
  isAdmin: boolean;
  reload: () => Promise<void>;
  /** GET /devices/status; null until loaded (the live columns then read as dashes). */
  statuses: DeviceStatus[] | null;
  /** Devices at either end of a live flow (the shell's GET /flows). */
  live: ReadonlySet<string>;
  now: number;
}

/** Mockup 03's inventory, with online state, last handshake and traffic from GET /devices/status. */
export function DeviceTable({ devices, allDevices, cidrOf, me, isAdmin, reload, statuses, live, now }: Props) {
  const s = useStyles();
  const [renaming, setRenaming] = useState<Device | null>(null);
  const [revoking, setRevoking] = useState<Device | null>(null);

  const cols: { key: string; label: string; width: string; end?: boolean }[] = [
    { key: 'state', label: '', width: '26px' },
    { key: 'name', label: 'Name', width: '180px' },
    { key: 'address', label: 'Address', width: '132px' },
    { key: 'network', label: 'Network', width: '84px' },
    { key: 'tags', label: 'Tags', width: 'auto' },
    ...(isAdmin ? [{ key: 'owner', label: 'Owner', width: '150px' }] : []),
    { key: 'handshake', label: 'Last handshake', width: '112px', end: true },
    { key: 'rx', label: 'Received', width: '78px', end: true },
    { key: 'tx', label: 'Sent', width: '72px', end: true },
    { key: 'menu', label: '', width: '40px' },
  ];

  return (
    <>
      <Table className={s.table} size="small" aria-label={isAdmin ? 'All enrolled devices' : 'Your enrolled devices'}>
        <TableHeader>
          <TableRow className={s.head}>
            {cols.map((c) => (
              <TableHeaderCell
                key={c.key}
                className={mergeClasses(s.cell, c.end && s.end)}
                style={{ width: c.width }}
                aria-label={c.key === 'state' ? 'Status' : c.key === 'menu' ? 'Actions' : undefined}
              >
                {c.label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((d) => {
            const address = (
              <span className={s.address}>
                <span className={s.mono}>{d.overlayIp}</span>
                {d.addressMode === 'reserved' && <PinMark />}
              </span>
            );
            const canRename = isAdmin || d.ownerSub === me.sub;
            const cells = statusCells(d.id, statuses, live, now);
            return (
              <TableRow key={d.id} className={s.row} data-device={d.name}>
                <TableCell className={s.cell}>
                  <PresenceDot className={s.dot} state={statuses ? cells.presence : null} />

                </TableCell>
                <TableCell className={s.cell}>
                  <span className={s.name}>
                    <span className={s.nameText}>{d.name}</span>
                    {d.dnsName && <span className={s.dns}>{d.dnsName}</span>}
                  </span>
                </TableCell>
                <TableCell className={s.cell}>
                  {isAdmin ? (
                    <AddressFlyout device={d} devices={allDevices} cidr={cidrOf(d.network)} onSaved={reload}>
                      {address}
                    </AddressFlyout>
                  ) : (
                    address
                  )}
                </TableCell>
                <TableCell className={s.cell}>
                  {d.network ? <Chip>{d.network}</Chip> : <span className={s.muted}>legacy</span>}
                </TableCell>
                <TableCell className={s.cell}>
                  <span className={s.chips}>
                    {d.tags.map((t) => (
                      <Chip key={t}>{t}</Chip>
                    ))}
                    {isAdmin && <TagsPopover device={d} onSaved={reload} />}
                  </span>
                </TableCell>
                {isAdmin && (
                  <TableCell className={s.cell}>
                    <span className={s.owner}>{d.ownerName ?? d.ownerSub}</span>
                  </TableCell>
                )}
                <TableCell className={mergeClasses(s.cell, s.end)}>
                  {cells.handshakeAt ? (
                    <Tooltip content={formatDateTime(cells.handshakeAt)} relationship="description">
                      <span className={s.mono}>{cells.handshake}</span>
                    </Tooltip>
                  ) : (
                    <span className={s.mono}>{cells.handshake}</span>
                  )}
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.end)}>
                  <span className={s.mono}>{cells.rx}</span>
                </TableCell>
                <TableCell className={mergeClasses(s.cell, s.end)}>
                  <span className={s.mono}>{cells.tx}</span>
                </TableCell>
                <TableCell className={s.cell}>
                  <Menu positioning="below-end">
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="transparent"
                        size="small"
                        icon={<MoreHorizontal20Regular />}
                        aria-label={`Actions for ${d.name}`}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem icon={<Rename16Regular />} disabled={!canRename} onClick={() => setRenaming(d)}>
                          Rename
                        </MenuItem>
                        <MenuItem icon={<Delete16Regular />} onClick={() => setRevoking(d)}>
                          Revoke
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <RenameDialog device={renaming} onClose={() => setRenaming(null)} onDone={reload} />
      <RevokeDialog device={revoking} onClose={() => setRevoking(null)} onDone={reload} />
    </>
  );
}
